import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { StoreActionLoggableName } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { CONVERSATION_LIST_LOAD_MORE_PAGE_SIZE } from './constants';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { createStoreError } from './createStoreError';
import { getProcessedConversationChannels } from './getProcessedConversationChannels';
import { isConversationListPaginationEnabled } from './isConversationListPaginationEnabled';
import { mergeConversationChannelTasks } from './mergeConversationChannelTasks';
import type { ChatStoreAction } from './types';

export const loadMoreConversationChannelsData: SafeAction<[]> = createSafeAction(
	StoreActionLoggableName.loadMoreConversationChannelsData,
	(): ChatStoreAction =>
		async (
			{ setState, getState },
			{ assistanceService, experienceTracker, unifiedTasksActions },
		) => {
			if (!isConversationListPaginationEnabled()) {
				return;
			}

			const { conversationPagination } = getState();
			if (
				conversationPagination.isLoadingMore ||
				!conversationPagination.hasMore ||
				!conversationPagination.nextCursor
			) {
				return;
			}

			setState({
				conversationPagination: {
					...conversationPagination,
					isLoadingMore: true,
					loadMoreError: null,
				},
			});

			experienceTracker.start({
				id: ExperienceName.LOAD_MORE_CONVERSATION_CHANNELS,
				name: ExperienceName.LOAD_MORE_CONVERSATION_CHANNELS,
			});

			try {
				const conversationChannelsResponse = await assistanceService.getConversationChannels({
					limit: CONVERSATION_LIST_LOAD_MORE_PAGE_SIZE,
					cursor: conversationPagination.nextCursor,
				});
				const channelsFromAPI = conversationChannelsResponse.items;
				const { conversations } = getState();
				const processedChannels = getProcessedConversationChannels({
					channelsFromAPI,
					conversations,
				});

				mergeConversationChannelTasks(channelsFromAPI, unifiedTasksActions);

				setState({
					conversations: {
						...conversations,
						...processedChannels,
					},
					conversationPagination: {
						...getState().conversationPagination,
						nextCursor: conversationChannelsResponse.cursor,
						hasMore: conversationChannelsResponse.hasNext,
						isLoadingMore: false,
						loadMoreError: null,
					},
				});

				experienceTracker.succeed({
					name: ExperienceName.LOAD_MORE_CONVERSATION_CHANNELS,
				});
			} catch (error) {
				experienceTracker.fail({
					name: ExperienceName.LOAD_MORE_CONVERSATION_CHANNELS,
					error: error instanceof Error ? error : new Error(String(error)),
				});

				setState({
					conversationPagination: {
						...getState().conversationPagination,
						isLoadingMore: false,
						loadMoreError: createStoreError(
							error,
							undefined,
							StoreActionLoggableName.loadMoreConversationChannelsData,
						),
					},
				});
			}
		},
);
